import type { ComponentProps, ReactElement, ReactNode } from "react";
import { Fragment } from "react";
import { Slot } from "radix-ui";
import { Link } from "react-router-dom";
import { tv } from "tailwind-variants";

import type { SvgSlot } from "@/shared/ui/common";
import { AltArrowRightIcon } from "../icons";
import { IconWrapper } from "./icons";

const styles = tv({
  slots: {
    list: "flex flex-wrap items-center gap-1.5 text-sm break-words text-neutral-600 hover:text-neutral-800 sm:gap-2.5",
    item: "inline-flex items-center gap-1.5",
    link: "flex items-center gap-1 font-medium transition-colors focus:underline focus:underline-offset-1 focus:outline-hidden",
    page: "flex items-center gap-1 font-semibold text-neutral-950",
  },
});

const { list, item, link, page } = styles();

interface BreadCrumbLink {
  href: string;
  icon?:
    | ReactElement
    | ((props: ComponentProps<typeof SvgSlot>) => JSX.Element);
  label: ReactNode;
}

const BreadcrumbRoot = ({
  ...props
}: ComponentProps<"nav"> & {
  separator?: ReactNode;
}) => <nav aria-label="breadcrumb" {...props} />;

const BreadcrumbList = ({ className, ...props }: ComponentProps<"ol">) => (
  <ol className={list({ className })} {...props} />
);

const BreadcrumbItem = ({ className, ...props }: ComponentProps<"li">) => (
  <li className={item({ className })} {...props} />
);

const BreadcrumbLink = ({
  asChild,
  className,
  ...props
}: ComponentProps<"a"> & {
  asChild?: boolean;
}) => {
  const Comp = asChild ? Slot.Root : "a";

  return <Comp className={link({ className })} {...props} />;
};

const BreadcrumbPage = ({ className, ...props }: ComponentProps<"span">) => (
  <span
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={page({ className })}
    {...props}
  />
);

const BreadcrumbSeparator = ({ children, ...props }: ComponentProps<"li">) => (
  <li role="presentation" aria-hidden="true" {...props}>
    {children ?? (
      <IconWrapper size="xs">
        <AltArrowRightIcon />
      </IconWrapper>
    )}
  </li>
);

export interface BreadcrumbProps {
  links: BreadCrumbLink[];
  className?: string;
}

export const Breadcrumb = ({ links, className }: BreadcrumbProps) => {
  return (
    <BreadcrumbRoot className={className}>
      <BreadcrumbList>
        {links.map((link, i) =>
          i + 1 !== links.length ? (
            <Fragment key={link.href}>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={link.href}>
                    {link.icon && (
                      <IconWrapper size="sm">
                        {typeof link.icon === "function"
                          ? link.icon({ className })
                          : link.icon}
                      </IconWrapper>
                    )}
                    {link.label}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </Fragment>
          ) : (
            <BreadcrumbItem key={link.href}>
              <BreadcrumbPage>
                {link.icon && (
                  <IconWrapper size="sm">
                    {typeof link.icon === "function"
                      ? link.icon({ className })
                      : link.icon}
                  </IconWrapper>
                )}
                {link.label}
              </BreadcrumbPage>
            </BreadcrumbItem>
          ),
        )}
      </BreadcrumbList>
    </BreadcrumbRoot>
  );
};
